import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer die Seite "Terminal" (`/settings/terminal`).
 * Wird ueber den `?`-Button im Settings-Sticky-Header gestartet.
 *
 * Sechs Schritte:
 *  1. Welcome + Terminal-Grundeinstellungen
 *  2. Mitarbeiterliste anzeigen (Privacy-Trade-off)
 *  3. Neues Terminal koppeln (Pairing-Code)
 *  4. PIN-Laenge (Sicherheit vs. Bedienkomfort)
 *  5. Admin-PIN fuer Manager-Aktionen
 *  6. Inaktivitaets-Timeout
 *
 * Tour-Texte in `de.json/en.json` unter `TUTORIAL.SETTINGS_TERMINAL.*`,
 * Voice-Over-Manifest unter `settings-terminal-help`.
 */
export const SETTINGS_TERMINAL_HELP_TOUR: TourConfig = {
  id: TourFeature.SettingsTerminalHelp,
  version: 1,
  title: 'SETTINGS_TERMINAL_HELP_TOUR_TITLE',
  description: 'SETTINGS_TERMINAL_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: { route: '/settings/terminal' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 110,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'SETTINGS_TERMINAL_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.TerminalBasicSection,
          spotlightAnchor: AnchorId.TerminalBasicSection,
          title: 'TUTORIAL.SETTINGS_TERMINAL.WELCOME.TITLE',
          content: 'TUTORIAL.SETTINGS_TERMINAL.WELCOME.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'employee-list',
          anchor: AnchorId.TerminalShowEmployeeList,
          spotlightAnchor: AnchorId.TerminalShowEmployeeList,
          title: 'TUTORIAL.SETTINGS_TERMINAL.EMPLOYEE_LIST.TITLE',
          content: 'TUTORIAL.SETTINGS_TERMINAL.EMPLOYEE_LIST.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'add-terminal',
          anchor: AnchorId.TerminalAddButton,
          spotlightAnchor: AnchorId.TerminalDevicesSection,
          title: 'TUTORIAL.SETTINGS_TERMINAL.ADD_TERMINAL.TITLE',
          content: 'TUTORIAL.SETTINGS_TERMINAL.ADD_TERMINAL.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          // Devices-Section ist nur sichtbar, wenn `enableTerminal` an ist
          // — sonst Anker nicht im DOM.
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="terminal-devices-section"]'),
        },
        {
          id: 'pin-length',
          anchor: AnchorId.TerminalPinLength,
          spotlightAnchor: AnchorId.TerminalPinLength,
          title: 'TUTORIAL.SETTINGS_TERMINAL.PIN_LENGTH.TITLE',
          content: 'TUTORIAL.SETTINGS_TERMINAL.PIN_LENGTH.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="terminal-pin-length"]'),
        },
        {
          id: 'admin-pin',
          anchor: AnchorId.TerminalRequireAdminPin,
          spotlightAnchor: AnchorId.TerminalAdminPinSection,
          title: 'TUTORIAL.SETTINGS_TERMINAL.ADMIN_PIN.TITLE',
          content: 'TUTORIAL.SETTINGS_TERMINAL.ADMIN_PIN.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="terminal-admin-pin-section"]'),
        },
        {
          id: 'timeout',
          anchor: AnchorId.TerminalPinpadTimeout,
          spotlightAnchor: AnchorId.TerminalPinpadTimeout,
          title: 'TUTORIAL.SETTINGS_TERMINAL.TIMEOUT.TITLE',
          content: 'TUTORIAL.SETTINGS_TERMINAL.TIMEOUT.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="terminal-pinpad-timeout"]'),
        },
      ],
    },
  ],
};
